import React from 'react';
import clsx from 'clsx';

const headingStyles = {
  h1: 'text-4xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-5xl',
  h2: 'text-3xl font-bold tracking-tight text-gray-900 dark:text-white sm:text-4xl',
  h3: 'text-2xl font-bold tracking-tight text-gray-900 dark:text-white',
  h4: 'text-xl font-bold tracking-tight text-gray-900 dark:text-white',
  h5: 'text-lg font-semibold tracking-tight text-gray-900 dark:text-white',
  h6: 'text-base font-semibold tracking-tight text-gray-900 dark:text-white',
};

const textStyles = {
  body: 'text-base text-gray-600 dark:text-gray-400',
  lead: 'text-lg text-gray-600 dark:text-gray-400 leading-relaxed',
  muted: 'text-sm text-gray-500 dark:text-gray-500',
  small: 'text-xs text-gray-500 dark:text-gray-500',
};

// Base Heading component
export const Heading = ({ 
  level = 'h2', 
  children, 
  className = '', 
  as,
  ...props 
}) => {
  const Component = as || level;
  return (
    <Component 
      className={clsx(headingStyles[level], className)} 
      {...props}
    >
      {children}
    </Component>
  );
};

// Convenience heading components
export const H1 = (props) => <Heading level="h1" {...props} />;
export const H2 = (props) => <Heading level="h2" {...props} />;
export const H3 = (props) => <Heading level="h3" {...props} />;
export const H4 = (props) => <Heading level="h4" {...props} />;
export const H5 = (props) => <Heading level="h5" {...props} />;
export const H6 = (props) => <Heading level="h6" {...props} />;

// Text component with variants
export const Text = ({ 
  variant = 'body', 
  children, 
  className = '', 
  as = 'p',
  ...props 
}) => {
  const Component = as;
  return (
    <Component 
      className={clsx(textStyles[variant], className)} 
      {...props}
    >
      {children}
    </Component>
  );
};

// Specialized components for specific use cases
export const Lead = (props) => <Text variant="lead" {...props} />;
export const Muted = (props) => <Text variant="muted" {...props} />;
export const Small = (props) => <Text variant="small" {...props} />;

// Maintain compatibility with previous API
export const Paragraph = (props) => <Text {...props} />;
export const Body = (props) => <Text variant="body" {...props} />;

// Link component with consistent styling
export const Link = ({ 
  children, 
  className = '', 
  href = '#',
  underline = true,
  ...props 
}) => {
  return (
    <a 
      href={href}
      className={clsx(
        'text-blue-600 hover:text-blue-800 dark:text-blue-400 dark:hover:text-blue-300',
        underline && 'underline underline-offset-2',
        className
      )} 
      {...props}
    >
      {children}
    </a>
  );
};
